import React from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  SentimentDissatisfied,
  SearchOff,
  FolderOff,
  ErrorOutline,
  SportsSoccer,
} from '@mui/icons-material';
import spacing from '../spacing';
import typography from '../typography';
import Button from './Button';

/**
 * EmptyState Component
 * @component
 * @description Empty state displays for various scenarios (no data, no results, errors)
 * 
 * @example
 * // No data state
 * <EmptyState type="no-data" />
 * 
 * @example
 * // Custom empty state with action
 * <EmptyState 
 *   type="no-matches"
 *   title="No hay partidos"
 *   description="Crea tu primer partido"
 *   onAction={handleCreate}
 *   actionLabel="Crear partido"
 * />
 * 
 * @param {string} type - 'no-data' | 'no-results' | 'no-matches' | 'no-players' | 'error' | 'empty-state'
 */

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: spacing[8],
  textAlign: 'center',
  minHeight: 300,
});

const IconContainer = styled(Box)(({ theme }) => ({
  marginBottom: spacing[4],
  color: theme.palette.text.disabled,
  
  '& .MuiSvgIcon-root': {
    fontSize: '4rem',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  ...typography.styles.h4,
  color: theme.palette.text.primary,
  marginBottom: spacing[2],
}));

const Description = styled(Typography)(({ theme }) => ({
  ...typography.styles.body,
  color: theme.palette.text.secondary,
  marginBottom: spacing[4],
  maxWidth: 400,
}));

const ActionContainer = styled(Box)({
  display: 'flex',
  gap: spacing[2],
  flexWrap: 'wrap',
  justifyContent: 'center',
});

const EmptyState = ({
  type = 'no-data',
  title,
  description,
  icon: CustomIcon,
  action,
  actionLabel,
  onAction,
  secondaryAction,
  secondaryActionLabel,
  onSecondaryAction,
  children,
}) => {
  // Configuración por defecto según tipo
  const configs = {
    'no-data': {
      icon: FolderOff,
      title: 'No hay datos disponibles',
      description: 'No se encontraron datos para mostrar en este momento.',
    },
    'no-results': {
      icon: SearchOff,
      title: 'No se encontraron resultados',
      description: 'Intenta ajustar los filtros o criterios de búsqueda.',
    },
    'no-matches': {
      icon: SportsSoccer,
      title: 'No hay partidos',
      description: 'Aún no se han registrado partidos para esta temporada.',
    },
    'no-players': {
      icon: SportsSoccer,
      title: 'No hay jugadores',
      description: 'No se encontraron jugadores que coincidan con los criterios.',
    },
    'error': {
      icon: ErrorOutline,
      title: 'Algo salió mal',
      description: 'Hubo un problema al cargar los datos. Por favor, intenta de nuevo.',
    },
    'empty-state': {
      icon: SentimentDissatisfied,
      title: 'Parece que está vacío',
      description: 'Comienza agregando algunos elementos para ver contenido aquí.',
    },
  };

  const config = configs[type] || configs['no-data'];
  const Icon = CustomIcon || config.icon;
  const finalTitle = title || config.title;
  const finalDescription = description || config.description;

  return (
    <Container>
      <IconContainer>
        <Icon />
      </IconContainer>
      
      <Title>{finalTitle}</Title>
      
      <Description>{finalDescription}</Description>
      
      {(action || onAction || secondaryAction || onSecondaryAction || children) && (
        <ActionContainer>
          {action ? (
            action
          ) : onAction ? (
            <Button variant="contained" color="primary" onClick={onAction}>
              {actionLabel || 'Reintentar'}
            </Button>
          ) : null}
          
          {secondaryAction ? (
            secondaryAction
          ) : onSecondaryAction ? (
            <Button variant="outlined" color="primary" onClick={onSecondaryAction}>
              {secondaryActionLabel || 'Cancelar'}
            </Button>
          ) : null}
          
          {children}
        </ActionContainer>
      )}
    </Container>
  );
};

// Variantes preconfiguradas para casos comunes
export const NoDataState = (props) => <EmptyState type="no-data" {...props} />;
export const NoResultsState = (props) => <EmptyState type="no-results" {...props} />;
export const NoMatchesState = (props) => <EmptyState type="no-matches" {...props} />;
export const NoPlayersState = (props) => <EmptyState type="no-players" {...props} />;
export const ErrorState = (props) => <EmptyState type="error" {...props} />;

export default EmptyState;
